import React, { useEffect, useRef } from 'react';
import { UpdateWorkspace } from '../../../../shared/types/workspaceTypes';
import styled from 'styled-components';
import { FlexRow } from '../../../../shared/FlexRow';
import { FlexCol } from '../../../../shared/FlexCol';
import { Body, Label, Title } from '../../../../shared/styled-components/styled_text';

interface Props {
  data: UpdateWorkspace;
  setOpenDetail: (open: boolean) => void;
}

export const AlimTokPreview: React.FC<Props> = ({ data, setOpenDetail }) => {
  const buttonRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!buttonRef.current || !tooltipRef.current) return;
    const buttonRect = buttonRef.current.getBoundingClientRect();
    tooltipRef.current.style.top = `${buttonRect.top - 18}px`;
    tooltipRef.current.style.left = `${buttonRect.left + buttonRect.width / 2}px`;
    tooltipRef.current.style.visibility = 'visible';
  }, [buttonRef.current]);

  return (
    <Container>
      <ContentWrapper>
        <Header>
          <div>인터뷰알리미 캔디데이트</div>
          <FlexRow width={'fit-content'} justify={'flex-start'}>
            1899-1057
            <img src={'/images/side-nav/dropdown_arrow.svg'} style={{ width: '1.2528rem', height: '1.2528rem' }} alt={''} />
          </FlexRow>
        </Header>
        <ChatWrapper>
          <img src={'/images/workspace-wizard/alim_profile.svg'} alt='alim profile' style={{ width: '2.8rem', height: '2.8rem' }} />
          <FlexCol align={'flex-start'} width={'fit-content'} gap={'0.2rem'}>
            <Body size={'s'} style={{ color: 'var(--content-color-secondary)', paddingLeft: '0.34rem' }}>
              인터뷰알리미 캔디데이트
            </Body>
            <ChatBubbleContainer>
              <Body
                size={'s'}
                style={{
                  width: '100%',
                  padding: '0.8rem 1.2rem',
                  color: 'var(--content-color-primary)',
                  backgroundColor: '#FEE800',
                }}
              >
                알림톡 도착
              </Body>
              <FlexCol width={'100%'} align={'flex-start'} gap={'0.8rem'} style={{ padding: '1.2rem' }}>
                <FlexCol
                  width={'100%'}
                  justify={'flex-start'}
                  align={'flex-start'}
                  style={{ paddingBottom: '1.2rem', borderBottom: '1px solid var(--border-color)' }}
                >
                  <Label size={'s'}>(알림)</Label>
                  <Title size={'s'} style={{ color: 'var(--content-color-primary)', display: 'flex' }}>
                    <CompanyName $filled={!!data.name}>{data.name ? data.name : '회사 이름'}</CompanyName>
                    안내
                  </Title>
                </FlexCol>
                <FlexCol width={'100%'} justify={'flex-start'} align={'flex-start'} gap={'0.2rem'}>
                  <WrappedLabel size={'s'} $filled={!!data.name}>
                    [{data.name ? data.name : '회사 이름'}]
                  </WrappedLabel>
                  <FlexRow width={'fit-content'} justify={'flex-start'} align={'center'} gap={'0.2rem'}>
                    <Body size={'s'}>지원하신</Body>
                    <WrappedLabel size={'s'} $filled={!!data.name}>
                      {data.name ? data.name : '회사 이름'}
                    </WrappedLabel>
                    <Body size={'s'}>에서</Body>
                  </FlexRow>
                  <Body size={'s'}>알림이 도착했습니다.</Body>
                </FlexCol>
                <FlexCol width={'100%'} justify={'flex-start'} align={'flex-start'} gap={'0.2rem'}>
                  <Body size={'s'}>변동사항 발생 시 연락해주세요.</Body>
                  <FlexRow width={'fit-content'} justify={'flex-start'} align={'center'} gap={'0.2rem'}>
                    <Body size={'s'}>연락처:</Body>
                    <WrappedLabel size={'s'} $filled={!!data.contact.officer_contact}>
                      {data.contact.officer_contact ? data.contact.officer_contact : '회사 연락처'}
                    </WrappedLabel>
                  </FlexRow>
                </FlexCol>

                <ButtonWrapper onClick={() => setOpenDetail(true)} ref={buttonRef}>
                  <Label size={'s'}>내게 온 알림 확인하기</Label>
                </ButtonWrapper>
              </FlexCol>
            </ChatBubbleContainer>
          </FlexCol>
        </ChatWrapper>
      </ContentWrapper>
      <TooltipWrapper ref={tooltipRef}>
        <TooltipBubble>
          <Body size={'s'} style={{ color: 'white' }}>
            💡 버튼을 클릭하면 지원자가 보는
            <br /> 안내페이지를 확인할 수 있어요.
          </Body>
        </TooltipBubble>
      </TooltipWrapper>
      <FakeFooter>
        <img src={'/images/workspace-wizard/home.svg'} alt='home' style={{ width: '1.879rem', height: '1.879rem' }} />
        <div>채널 홈 바로가기</div>
      </FakeFooter>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: calc(100% - 5.6rem);
  background-color: #c6d6e3;
  position: relative;
  display: flex;
  flex-direction: column;

  padding: env(safe-area-inset-top, 0px) env(safe-area-inset-right, 0px) env(safe-area-inset-bottom, 0px) env(safe-area-inset-left, 0px);

  * {
    box-sizing: border-box;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  flex: 1;
  padding: 2rem 1.6rem;
  overflow-y: auto;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
`;

const FakeFooter = styled.div`
  width: 100%;
  height: fit-content;
  padding-block: 0.84rem;
  background-color: var(--background-1);

  /* 하단에 고정 */
  position: sticky;
  bottom: 0;

  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 0.42rem;

  > div {
    color: var(--content-color-primary);
    font-size: 1.4617rem;
    font-weight: 300;
    line-height: 120%;
  }
`;

const Header = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 0.42rem;

  & > div:first-child {
    color: black;
    font-size: 1.6705rem;
    font-weight: 600;
    line-height: 1.7746rem;
  }

  & > div:last-child {
    color: var(--content-color-secondary);
    font-size: 1.2528rem;
    font-weight: 400;
    line-height: 120%;
  }
`;

const ChatWrapper = styled.div`
  width: 100%;
  height: fit-content;
  box-sizing: border-box;
  padding-left: 1.2rem;

  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 0.4rem;
`;

const ChatBubbleContainer = styled.div`
  width: 26.6rem;
  min-width: 26.6rem;
  max-width: 26.6rem;
  height: fit-content;
  background-color: var(--background-1);
  border-radius: 6px;
  overflow: hidden;
  position: relative;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
`;

const CompanyName = styled.span<{ $filled?: boolean }>`
  display: inline-block;
  width: fit-content;
  max-width: 12rem;
  padding-inline: 0.4rem;
  margin-right: 0.4rem;
  background-color: var(--mono-op-12);
  border-radius: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  color: var(--content-color-${props => (props.$filled ? 'primary' : 'tertiary')});
`;

const WrappedLabel = styled(Label)<{ $filled?: boolean }>`
  width: fit-content;
  max-width: 12rem;
  padding-inline: 0.4rem;
  margin-bottom: 0.2rem;
  background-color: var(--mono-op-12);
  border-radius: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--content-color-${props => (props.$filled ? 'primary' : 'tertiary')});
`;

const ButtonWrapper = styled.div`
  width: 100%;
  padding-block: 0.6rem;
  background-color: var(--background-2);
  border-radius: 6px;
  cursor: pointer;

  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 0.8rem;
`;

const TooltipWrapper = styled.div`
  width: fit-content;
  height: fit-content;
  position: absolute;
  visibility: hidden;
  transform: translateX(-50%);
  z-index: 3000;
`;

const TooltipBubble = styled.div`
  width: 24rem;
  height: fit-content;
  padding-block: 0.6rem;
  border-radius: 4px;
  background-color: #1b1d1fe5;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  &:before {
    content: '';
    position: absolute;
    top: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-bottom: 10px solid #1b1d1fe5;
  }
`;
